/**
 * /help support-form client bundle entry point.
 *
 * WHAT THIS FILE DOES:
 * - Boots the /help support-form client bundle.
 * - Initializes Firebase Auth using the injected config.
 * - Renders login UI for signed-out users.
 * - Hands off to SupportFormController for signed-in users.
 *
 * WHY THIS EXISTS:
 * - /help is a public SSR page that needs client-side auth + form logic.
 * - Auth must happen in the browser (Firebase JS SDK).
 * - Keeps support-form logic isolated from admin/app bundles.
 *
 * ARCHITECTURE CONTEXT:
 * - packages/backend/src/ssr/help-contact.ts injects window.__GIST_HELP_CONFIG__.
 * - This bundle mounts into #help-root defined in the SSR shell.
 * - Support form logic lives in support-form-controller.ts to avoid a god module.
 *
 * CALLED BY:
 * - Browser when the /help page loads (via script type="module").
 *
 * THIS FILE CALLS:
 * - firebase/app initializeApp()
 * - firebase/auth getAuth(), onAuthStateChanged(), signInWithEmailAndPassword()
 * - SupportFormController for authenticated support submissions
 *
 * ERROR HANDLING:
 * - Missing config renders a friendly error message.
 * - Auth errors surface inline in the login form.
 *
 * RELATED FILES:
 * - packages/backend/src/ssr/help-contact.ts (SSR shell)
 * - packages/backend/src/client/help-contact/support-form-controller.ts (form)
 * - packages/backend/public/static/help-contact.css (styles)
 *
 * URL history:
 * - Originally served at /help. Briefly moved to /help/contact while a
 *   help-center article index lived at /help. The articles were later
 *   merged into /docs/** and the form moved back to /help.
 */

import { initializeApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  type Auth,
  type User
} from 'firebase/auth';
import { createElement, createSvgIcon } from './help-contact/dom.js';
import { SupportFormController } from './help-contact/support-form-controller.js';
import { createWebAuthBridge, type WebAuthBridge, type WebAuthState } from './web-auth-bridge.js';
import { shouldShowSignedOutWebUi } from './web-auth-session-gate.js';
import { resolveUserEmail, resolveUserEmailWithReload } from '@gist-protocol/core';
import { clearWebWorkerSession, mintWebWorkerSession } from './web-session-cookie.js';

/** When the extension is absent, stop waiting for gist-auth-state before showing login. */
const EXTENSION_AUTH_PROBE_MS = 500;

/**
 * Firebase config shape (matches admin dashboard).
 */
interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
}

declare global {
  interface Window {
    __GIST_HELP_CONFIG__?: FirebaseConfig | null;
  }
}

/**
 * UI state enum for the help page.
 */
const HelpState = {
  LOADING: 'loading',
  LOGIN: 'login',
  FORM: 'form',
  ERROR: 'error'
} as const;

type HelpStateValue = (typeof HelpState)[keyof typeof HelpState];

/**
 * Main help page controller.
 */
class HelpPage {
  /** Root mount element for the help experience. */
  private root: HTMLElement;
  /** Firebase auth instance (initialized from injected config). */
  private auth: Auth | null = null;
  /** Currently signed-in Firebase user. */
  private user: User | null = null;
  /** Latest auth state reported by the extension (worker token). */
  private extensionAuth: WebAuthState | null = null;
  /** Active UI state (loading/login/form/error). */
  private state: HelpStateValue = HelpState.LOADING;
  /** Last login error copy displayed to the user. */
  private loginError: string = '';
  /** Support form controller when authenticated. */
  private supportForm: SupportFormController | null = null;
  /** Web auth bridge for extension sync. */
  private webAuthBridge: WebAuthBridge;
  /** Guard to avoid echoing extension-triggered logout back to extension. */
  private suppressLogoutEcho: boolean = false;
  /** In-flight sign-out triggered by extension sync. */
  private extensionLogoutInFlight: boolean = false;
  /** Last UID shared with extension to avoid duplicate imports. */
  private lastImportedUid: string | null = null;
  /** False until the extension answers gist-auth-request (may be null = signed out). */
  private extensionAuthResolved = false;

  constructor(root: HTMLElement) {
    this.root = root;
    this.webAuthBridge = createWebAuthBridge({
      onAuthState: (state) => {
        void this.handleExtensionAuthState(state);
      }
    });
  }

  /**
   * Initialize Firebase and observe auth state.
   */
  async init(): Promise<void> {
    const config = window.__GIST_HELP_CONFIG__;

    if (!config) {
      this.renderError('Help is temporarily unavailable. Please try again later.');
      return;
    }

    try {
      const app = initializeApp(config);
      this.auth = getAuth(app);
      this.webAuthBridge.listen();
      window.setTimeout(() => {
        if (this.extensionAuthResolved) {
          return;
        }
        this.extensionAuthResolved = true;
        this.applyAuthUi();
      }, EXTENSION_AUTH_PROBE_MS);

      onAuthStateChanged(this.auth, (user) => {
        const previousUser = this.user;
        this.user = user;
        if (!user) {
          this.loginError = '';
        }
        void this.syncAuthWithExtension(user, previousUser);
        this.applyAuthUi();
      });
    } catch (error) {
      console.error('Firebase init failed:', error);
      this.renderError('Help is temporarily unavailable. Please try again later.');
    }
  }

  /**
   * Mirrors extension auth state changes back into the web session.
   *
   * When the extension logs out, we sign out of Firebase to keep both surfaces aligned.
   */
  private resolveHelpState(): HelpStateValue {
    if (!this.extensionAuthResolved) {
      return HelpState.LOADING;
    }
    if (!shouldShowSignedOutWebUi({
      firebaseUser: this.user,
      workerToken: this.extensionAuth?.workerToken ?? null
    })) {
      return HelpState.FORM;
    }
    return HelpState.LOGIN;
  }

  private applyAuthUi(): void {
    this.state = this.resolveHelpState();
    this.render();
  }

  private async resolveAuthorizationHeader(): Promise<string | null> {
    if (this.extensionAuth?.workerToken) {
      return `Bearer ${this.extensionAuth.workerToken}`;
    }
    if (this.user && !this.user.isAnonymous) {
      try {
        const idToken = await this.user.getIdToken();
        return `Bearer ${idToken}`;
      } catch (error) {
        console.warn('[gist-help] getIdToken failed during session sync', error);
      }
    }
    return null;
  }

  private async ensureWebWorkerSessionCookie(): Promise<void> {
    const authorization = await this.resolveAuthorizationHeader();
    if (!authorization) {
      return;
    }
    await mintWebWorkerSession({
      authorization,
      clientVersion: 'web-help',
      logPrefix: '[gist-help]'
    });
  }

  private async clearWebWorkerSessionCookie(): Promise<void> {
    const authorization = await this.resolveAuthorizationHeader();
    await clearWebWorkerSession({
      authorization,
      clientVersion: 'web-help',
      logPrefix: '[gist-help]'
    });
  }

  private async handleExtensionAuthState(state: WebAuthState | null): Promise<void> {
    this.extensionAuthResolved = true;
    this.extensionAuth = state;

    if (!state) {
      await this.clearWebWorkerSessionCookie();
    }

    if (!state && this.user && this.auth) {
      if (this.extensionLogoutInFlight) {
        return;
      }
      this.extensionLogoutInFlight = true;
      this.suppressLogoutEcho = true;
      try {
        await signOut(this.auth);
      } catch (error) {
        console.warn('Failed to sign out after extension logout', error);
        this.suppressLogoutEcho = false;
      } finally {
        this.extensionLogoutInFlight = false;
      }
      return;
    }

    if (state?.workerToken) {
      void this.ensureWebWorkerSessionCookie();
    }
    this.applyAuthUi();
  }

  /**
   * Sync Firebase auth changes to the extension.
   *
   * - On login: send Firebase idToken to extension for worker session exchange.
   * - On logout: notify extension unless logout originated from extension.
   */
  private async syncAuthWithExtension(nextUser: User | null, previousUser: User | null): Promise<void> {
    if (!this.webAuthBridge) {
      return;
    }

    if (nextUser) {
      if (nextUser.isAnonymous) {
        return;
      }

      const uid = nextUser.uid ?? null;
      if (uid && uid === this.lastImportedUid) {
        return;
      }

      try {
        const idToken = await nextUser.getIdToken();
        await mintWebWorkerSession({
          authorization: `Bearer ${idToken}`,
          clientVersion: 'web-help',
          logPrefix: '[gist-help]'
        });
        // Resolve across every provider so Google/Apple users don't land
        // in the extension with a null email on the first sync.
        const resolvedEmail = await resolveUserEmailWithReload(nextUser);
        this.webAuthBridge.sendAuthImport({
          idToken,
          email: resolvedEmail
        });
        this.lastImportedUid = uid;
      } catch (error) {
        console.warn('Failed to sync web auth to extension', error);
      }
      return;
    }

    this.lastImportedUid = null;

    if (previousUser && this.suppressLogoutEcho) {
      this.suppressLogoutEcho = false;
      return;
    }

    if (previousUser) {
      this.webAuthBridge.sendLogout();
    }
  }

  /**
   * Render the current state.
   */
  private render(): void {
    this.supportForm?.dispose();
    this.supportForm = null;

    switch (this.state) {
      case HelpState.LOADING:
        this.renderLoading();
        break;
      case HelpState.LOGIN:
        this.renderLogin();
        break;
      case HelpState.FORM:
        this.renderForm();
        break;
      case HelpState.ERROR:
        break;
    }
  }

  /**
   * Render loading spinner.
   */
  private renderLoading(): void {
    this.root.replaceChildren(
      createElement('div', { class: 'help-loading' },
        createElement('div', { class: 'help-loading__spinner' }),
        createElement('p', { class: 'help-loading__text' }, 'Loading...')
      )
    );
  }

  /**
   * Render error message.
   */
  private renderError(message: string): void {
    this.state = HelpState.ERROR;

    const iconDiv = createElement('div', { class: 'help-page__icon' });
    const errorIcon = createSvgIcon('0 0 24 24', ['M12 8v4', 'M12 16h.01', 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20']);
    iconDiv.appendChild(errorIcon);

    this.root.replaceChildren(
      createElement('div', { class: 'help-page' },
        createElement('header', { class: 'help-page__header' },
          iconDiv,
          createElement('h1', { class: 'help-page__title' }, 'Something went wrong'),
          createElement('p', { class: 'help-page__subtitle' }, message)
        )
      )
    );
  }

  /**
   * Render login form.
   */
  private renderLogin(): void {
    const page = createElement('div', { class: 'help-page' });

    const header = createElement('header', { class: 'help-page__header' });
    header.appendChild(createElement('h1', { class: 'help-page__title' }, "We're here to help"));
    header.appendChild(createElement('p', { class: 'help-page__subtitle' }, "Sign in to send us a message. We'll respond to your account email."));
    page.appendChild(header);

    const loginDiv = createElement('div', { class: 'help-login' });

    if (this.loginError) {
      loginDiv.appendChild(createElement('p', { class: 'help-login__error', role: 'alert' }, this.loginError));
    }

    const form = createElement('form', { id: 'help-login-form', class: 'help-login__form' });

    const emailField = createElement('div', { class: 'help-form-field' });
    emailField.appendChild(createElement('label', { for: 'help-email' }, 'Email'));
    emailField.appendChild(createElement('input', {
      type: 'email',
      id: 'help-email',
      name: 'email',
      required: true,
      autocomplete: 'email',
      placeholder: 'you@example.com'
    }));
    form.appendChild(emailField);

    const passwordField = createElement('div', { class: 'help-form-field' });
    passwordField.appendChild(createElement('label', { for: 'help-password' }, 'Password'));
    passwordField.appendChild(createElement('input', {
      type: 'password',
      id: 'help-password',
      name: 'password',
      required: true,
      autocomplete: 'current-password',
      placeholder: 'Your password'
    }));
    form.appendChild(passwordField);

    form.appendChild(createElement('button', { type: 'submit', class: 'help-btn help-btn--primary' }, 'Sign in'));
    loginDiv.appendChild(form);

    const divider = createElement('div', { class: 'help-login__divider' });
    divider.appendChild(createElement('span', {}, 'or'));
    loginDiv.appendChild(divider);

    const googleBtn = createElement('button', { id: 'help-google-btn', type: 'button', class: 'help-btn help-btn--google' });
    const googleSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    googleSvg.setAttribute('viewBox', '0 0 24 24');
    googleSvg.setAttribute('width', '20');
    googleSvg.setAttribute('height', '20');

    const googlePaths = [
      { d: 'M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z', fill: '#4285F4' },
      { d: 'M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z', fill: '#34A853' },
      { d: 'M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z', fill: '#FBBC05' },
      { d: 'M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z', fill: '#EA4335' }
    ];

    for (const { d, fill } of googlePaths) {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', d);
      path.setAttribute('fill', fill);
      googleSvg.appendChild(path);
    }

    googleBtn.appendChild(googleSvg);
    googleBtn.appendChild(document.createTextNode('Continue with Google'));
    loginDiv.appendChild(googleBtn);

    const note = createElement('p', { class: 'help-login__note' });
    note.appendChild(document.createTextNode("Don't have an account? "));
    note.appendChild(createElement('a', { href: 'https://gist.is', target: '_blank', rel: 'noopener noreferrer' }, 'Get Gist'));
    loginDiv.appendChild(note);

    page.appendChild(loginDiv);
    this.root.replaceChildren(page);
    this.bindLoginEvents();

    const emailInput = document.getElementById('help-email') as HTMLInputElement | null;
    if (emailInput) {
      requestAnimationFrame(() => {
        emailInput.focus();
      });
    }
  }

  /**
   * Bind login form event handlers.
   */
  private bindLoginEvents(): void {
    const form = document.getElementById('help-login-form') as HTMLFormElement | null;
    const googleBtn = document.getElementById('help-google-btn');

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!this.auth) return;

      const email = (document.getElementById('help-email') as HTMLInputElement)?.value?.trim();
      const password = (document.getElementById('help-password') as HTMLInputElement)?.value;

      if (!email || !password) {
        this.loginError = 'Please enter your email and password.';
        this.renderLogin();
        return;
      }

      const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Signing in...';
      }

      try {
        await signInWithEmailAndPassword(this.auth, email, password);
      } catch (error: unknown) {
        console.error('Sign in failed:', error);
        const code = (error as { code?: string })?.code ?? '';
        this.loginError = this.getAuthErrorMessage(code);
        this.renderLogin();
      }
    });

    googleBtn?.addEventListener('click', async () => {
      if (!this.auth) return;
      const btn = googleBtn as HTMLButtonElement;
      btn.disabled = true;
      try {
        const provider = new GoogleAuthProvider();
        await signInWithPopup(this.auth, provider);
      } catch (error: unknown) {
        console.error('Google sign in failed:', error);
        const code = (error as { code?: string })?.code ?? '';
        if (code !== 'auth/popup-closed-by-user') {
          this.loginError = this.getAuthErrorMessage(code);
          this.renderLogin();
        }
      } finally {
        btn.disabled = false;
      }
    });
  }

  /**
   * Get user-friendly auth error message.
   */
  private getAuthErrorMessage(code: string): string {
    switch (code) {
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Invalid email or password.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      default:
        return 'Sign in failed. Please try again.';
    }
  }

  /**
   * Render the support form for authenticated users.
   */
  private renderForm(): void {
    const workerToken = this.extensionAuth?.workerToken ?? null;
    if (shouldShowSignedOutWebUi({ firebaseUser: this.user, workerToken })) {
      this.renderLogin();
      return;
    }

    const displayEmail = this.extensionAuth?.email
      ?? (this.user ? resolveUserEmail(this.user) : null);

    this.supportForm = new SupportFormController({
      root: this.root,
      user: this.user,
      workerToken,
      displayEmail,
      onSignOut: async () => {
        await this.clearWebWorkerSessionCookie();
        this.extensionAuth = null;
        this.webAuthBridge.sendLogout();
        if (this.auth) {
          await signOut(this.auth);
        }
        this.applyAuthUi();
      }
    });

    this.supportForm.render();
  }
}

/**
 * Bootstrap the help page on DOM ready.
 */
function bootstrap(): void {
  const root = document.getElementById('help-root');
  if (!root) {
    console.error('Help root element not found');
    return;
  }

  const page = new HelpPage(root);
  void page.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
